// SPDX-License-Identifier: Elastic-2.0
/**
 * `useUiPrefs` — Zustand store for persistent dashboard chrome UI prefs (T-020).
 *
 * Currently owns one slot:
 *
 *   - `sidebarCollapsed` — whether the persistent sidebar is collapsed to its
 *     64px icon-only rail. Persisted to `localStorage["mirlai_ui_prefs"]` so
 *     a user's collapse choice survives reloads.
 *
 * Why a dedicated store (instead of stuffing this onto `useTheme` or a
 * generic "settings" bucket)?
 *
 *   - Theme is a global concern (read by `<html data-theme>` at first
 *     paint); UI prefs are chrome-only and never touch the inline boot
 *     script. Mixing them would force the boot script to read more JSON.
 *   - Keeping prefs feature-local (`features/dashboard/`) means the
 *     onboarding wizard, sign-in, and offline routes can render without
 *     pulling this module — Vite's tree-shaker drops it from those chunks.
 *
 * Hydration: reads on store creation. Writes are best-effort — privacy
 * mode / disabled storage throws are swallowed (the user's choice still
 * applies for the session, just won't persist across reloads).
 */
import { create } from "zustand";

/** localStorage key. Versioned-by-rename if the shape changes later. */
export const UI_PREFS_STORAGE_KEY = "mirlai_ui_prefs";

/** Persisted shape. Keep additive — new fields default safely. */
interface PersistedUiPrefs {
  sidebarCollapsed?: boolean;
}

export interface UiPrefsState {
  /** True when the sidebar is collapsed to its 64px icon-only rail. */
  sidebarCollapsed: boolean;
  /** Replace the collapsed flag (also persists). */
  setSidebarCollapsed: (next: boolean) => void;
  /** Convenience toggler — flips current value + persists. */
  toggleSidebarCollapsed: () => void;
}

function readStored(): PersistedUiPrefs {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(UI_PREFS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object") {
      return parsed as PersistedUiPrefs;
    }
    return {};
  } catch {
    // Storage disabled / corrupt JSON — fall back to defaults.
    return {};
  }
}

function writeStored(prefs: PersistedUiPrefs): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(UI_PREFS_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Storage may be disabled — chrome state still applies for the session.
  }
}

const initial = readStored();

export const useUiPrefs = create<UiPrefsState>((set, get) => ({
  sidebarCollapsed: initial.sidebarCollapsed ?? false,

  setSidebarCollapsed(next) {
    writeStored({ ...readStored(), sidebarCollapsed: next });
    set({ sidebarCollapsed: next });
  },

  toggleSidebarCollapsed() {
    const next = !get().sidebarCollapsed;
    writeStored({ ...readStored(), sidebarCollapsed: next });
    set({ sidebarCollapsed: next });
  },
}));
